import classNames from "classnames";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { Collapse } from "react-collapse";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";

interface StepRowProps {
  activeStep: number;
  stepNumber: number;
  text: React.ReactElement;
}

function StepRow({ activeStep, stepNumber, text }: StepRowProps) {
  const active = activeStep === stepNumber;
  const inactive = activeStep < stepNumber;
  const finished = activeStep > stepNumber;

  return (
    <div
      className={classNames("search-steps-mobile-menu-row", {
        "search-steps-mobile-menu-row-inactive": inactive,
      })}
    >
      <span
        className={classNames("search-steps-mobile-circle", {
          "search-steps-mobile-circle-active": active,
          "search-steps-mobile-circle-finished": finished,
        })}
      >
        {finished ? (
          <Img src="/static/images/ico-check.svg" alt="check icon" />
        ) : (
          stepNumber
        )}
      </span>
      <span
        className={classNames("search-steps-mobile-label", {
          "search-steps-mobile-label-active": active,
          "search-steps-mobile-label-finished": finished,
        })}
      >
        {text}
      </span>
    </div>
  );
}

type Props = {
  page: string;
};

function SearchStepsMobile({ page }: Props) {
  const [showMenu, changeMenuState] = useState(false);
  const router = useRouter();

  function toggleMenuState() {
    changeMenuState((prevState) => !prevState);
  }

  const policyKeyExists = router.query?.policyFailed !== undefined;

  let activeStep = 1;
  if (page === "result" || page === "hotel") {
    activeStep = policyKeyExists ? 3 : 2;
  }
  if (page === "booked" || page === "hotelBook") {
    activeStep = 3;
  }
  if (page === "hotelBooked") {
    activeStep = 4;
  }

  const chooseFlight = page.includes("hotel") ? (
    <FormattedMessage id="SearchSteps.chooseHotel" />
  ) : (
    <FormattedMessage id="FlightOffer.pickflight" />
  );

  const yourInformation = <FormattedMessage id="SearchSteps.yourInformation" />;
  const sendReservation = <FormattedMessage id="Result.confirmBooking" />;
  const selectRooms = <FormattedMessage id="SearchSteps.selectRooms" />;

  const searchStepsMenu = (
    <div className="search-steps-mobile-menu">
      <StepRow activeStep={activeStep} stepNumber={1} text={chooseFlight} />
      {page.includes("hotel") && (
        <StepRow
          activeStep={activeStep}
          stepNumber={2}
          text={<FormattedMessage id="SearchSteps.selectRooms" />}
        />
      )}
      <StepRow
        activeStep={activeStep}
        stepNumber={page.includes("hotel") ? 3 : 2}
        text={yourInformation}
      />
      <StepRow
        activeStep={activeStep}
        stepNumber={page.includes("hotel") ? 4 : 3}
        text={sendReservation}
      />
    </div>
  );

  let currentSearchStepText = { order: 1, text: chooseFlight };
  if (page === "result") {
    currentSearchStepText = { order: 2, text: yourInformation };
  } else if (page === "hotel") {
    currentSearchStepText = {
      order: policyKeyExists ? 3 : 2,
      text: policyKeyExists ? yourInformation : selectRooms,
    };
  } else if (page === "hotelBooked") {
    currentSearchStepText = { order: 4, text: sendReservation };
  } else if (page === "booked") {
    currentSearchStepText = { order: 3, text: sendReservation };
  }

  return (
    <div className="search-steps-mobile-wrapper">
      <div
        className="search-steps-mobile"
        onClick={toggleMenuState}
        role="button"
      >
        <span className="search-steps-circle-active">
          {currentSearchStepText.order}
        </span>
        <span className="search-steps-label search-steps-label-active">
          {currentSearchStepText.text}
        </span>
        <Img
          src="/static/images/ico-arrow-down.svg"
          className={classNames({
            "select-arrow-rotate-up": showMenu,
          })}
          alt="down arrow icon"
        />
      </div>
      <Collapse isOpened={showMenu}>{searchStepsMenu}</Collapse>
    </div>
  );
}

export default SearchStepsMobile;
